import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Upload } from 'lucide-react'
import RichText from '@/components/RichText'

const inputCls = 'px-3 py-2 border border-gray-200 rounded-lg text-sm'

const isHttpUrl = (v: string) => /^https?:\/\/\S+$/i.test(v.trim())

/**
 * The photo + email signature an agent sends with — upload/replace/remove the photo (or paste a
 * URL), write the signature, and see it as clients will. Shared by an admin's Team → Edit and the
 * agent's own My profile, so both edit the same thing the same way.
 */
export default function EmailIdentityEditor({
  photoUrl,
  onPhotoUrlChange,
  signature,
  onSignatureChange,
  onUpload,
  uploading,
  photoError,
}: {
  photoUrl: string
  onPhotoUrlChange: (url: string) => void
  signature: string
  onSignatureChange: (html: string) => void
  onUpload: (file: File) => void
  uploading: boolean
  photoError?: string | null
}) {
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null)
  // An uploaded avatar is a same-origin URL (/api/users/{id}/photo); a pasted one is http(s).
  // Either counts as a showable photo once it hasn't failed to load.
  const hasPhoto = photoUrl.trim() !== '' && brokenUrl !== photoUrl

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file after a failed upload
    if (f) onUpload(f)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {hasPhoto ? (
          <img
            src={photoUrl.trim()}
            alt=""
            onError={() => setBrokenUrl(photoUrl)}
            className="w-12 h-12 rounded-full object-cover border border-gray-200 shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-100 border border-gray-200 shrink-0" />
        )}
        <div className="flex flex-col gap-1">
          <label className="inline-flex items-center gap-1.5 text-sm text-brand-primary cursor-pointer hover:underline">
            <Upload size={14} />
            {uploading ? 'Uploading…' : hasPhoto ? 'Replace photo' : 'Upload photo'}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onPick} disabled={uploading} />
          </label>
          {hasPhoto && (
            <button type="button" onClick={() => onPhotoUrlChange('')} className="text-[11px] text-gray-400 hover:text-danger text-left">
              Remove photo
            </button>
          )}
          <span className="text-[11px] text-gray-400">PNG, JPEG, WEBP or GIF, up to 2&nbsp;MB.</span>
        </div>
      </div>
      {photoError && <p className="text-danger text-[11px]">{photoError}</p>}
      <input
        className={`${inputCls} w-full`}
        placeholder="…or paste an image URL (https://…)"
        value={isHttpUrl(photoUrl) || photoUrl === '' ? photoUrl : ''}
        onChange={(e) => onPhotoUrlChange(e.target.value)}
      />
      <SignatureField value={signature} onChange={onSignatureChange} />
      {(signature.trim() !== '' || hasPhoto) && (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-2">How it looks in an email</p>
          {/* Laid out as the sent email lays it out (MessageService.signatureHtml): the text, then the
              64px photo under it — so this preview and the compose preview agree. */}
          <div className="text-sm text-brand-dark whitespace-pre-line min-w-0 [&_a]:text-brand-primary [&_a]:underline">
            <RichText html={signature} variant="email" />
          </div>
          {/* A pasted signature with its own picture doesn't get the profile photo as well (as when sent). */}
          {hasPhoto && !/<img\b/i.test(signature) && (
            <img src={photoUrl.trim()} alt="" width={64} height={64} className="mt-1 w-16 h-16 object-cover" />
          )}
        </div>
      )}
    </div>
  )
}

/** Old signatures are plain lines of text; show them with their breaks in the visual editor. */
function asHtml(value: string): string {
  return /<(table|div|p|br|span|font|img|b|strong|a)\b/i.test(value) ? value : value.replace(/\n/g, '<br>')
}

/**
 * The signature itself, edited as it looks — so the signature an agent already has in Gmail or Outlook
 * can simply be copied there and pasted here, logo, layout and all (the server keeps a safe subset).
 * "Edit as HTML" is there for anyone handed the signature as code.
 */
function SignatureField({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const [mode, setMode] = useState<'visual' | 'html'>('visual')
  const ref = useRef<HTMLDivElement>(null)
  // Uncontrolled while typing: write the value in only when it changed from outside (load, HTML mode).
  useEffect(() => {
    const el = ref.current
    if (el && mode === 'visual' && el.innerHTML !== asHtml(value) && document.activeElement !== el) {
      el.innerHTML = asHtml(value)
    }
  }, [value, mode])
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-500">Signature</span>
        <button
          type="button"
          onClick={() => setMode((m) => (m === 'visual' ? 'html' : 'visual'))}
          className="text-[11px] text-brand-primary hover:underline"
        >
          {mode === 'visual' ? 'Edit as HTML' : 'Back to the visual editor'}
        </button>
      </div>
      {mode === 'visual' ? (
        <div
          ref={ref}
          role="textbox"
          aria-multiline="true"
          aria-label="Email signature"
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => {
            const el = e.target as HTMLDivElement
            // Deleting everything leaves a stray <br> behind — that's an empty signature, not one.
            onChange(el.textContent?.trim() || el.querySelector('img') ? el.innerHTML : '')
          }}
          data-placeholder="Type your signature, or copy it from Gmail / Outlook and paste it here"
          className={`${inputCls} w-full min-h-[7rem] max-h-80 overflow-auto bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40 empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 [&_img]:max-w-[240px] [&_a]:text-brand-primary [&_a]:underline`}
        />
      ) : (
        <textarea
          rows={8}
          aria-label="Email signature HTML"
          className={`${inputCls} w-full resize-y font-mono text-xs`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <p className="text-[11px] text-gray-400 mt-1">
        Added to the end of the emails you send. Already have one in Gmail or Outlook? Copy it there and paste it here.
      </p>
    </div>
  )
}
